import { computed, inject, Provider, signal } from '@angular/core';

import { ATTENTION_PROJECTIONS, AttentionProjection } from '@shared/services/attention/attention-projections';
import { AttentionBadge } from '@shared/services/attention/attention.models';
import { CapabilityService } from '@shared/services/capability/capability.service';
import { OpenWork } from '@shared/services/capability/open-work.models';

/** The rail items the restaurant's badges decorate — the pages-config urls of its children. */
export const RESTAURANT_RAIL = {
  kitchen: '/provider/app/restaurant/kitchen',
  floor: '/provider/app/restaurant/floor',
} as const;

/* What the restaurant store last counted, published here so the ROOT projection reads it without
   importing the store — which would pull the whole restaurant into the app's initial chunk (the
   7 MB budget, M2). Until the restaurant has been opened this session there is nothing counted, and
   no badge: the pass is the tenant's (Firestore), and following it from app start for every tenant
   would be a listener nobody asked for. */
const KITCHEN = signal<AttentionBadge>({ count: 0, tone: 'primary' });
const OCCUPIED = signal(0);
/* The live seats behind `restaurant.floor` (kzd r1b WI-0b) — `null` until the store has read the tenant's floor this
   session: an unknown count is `pending`, so a winding floor keeps winding instead of retiring on a zero nobody read. */
const SEATS = signal<number | null>(null);

/** The store's one write — the Kitchen's count and tone, and the Floor's occupancy. */
export function publishRestaurantBadges(kitchen: AttentionBadge, occupied: number): void {
  if (KITCHEN().count !== kitchen.count || KITCHEN().tone !== kitchen.tone) {
    KITCHEN.set(kitchen);
  }
  if (OCCUPIED() !== occupied) {
    OCCUPIED.set(occupied);
  }
}

/**
 * The store's other write (kzd r1b WI-0b): how many tables are still seated — live seat documents, not merged — or
 * `null` while the tenant's floor has not answered (a new tenant, before the restaurant has been opened).
 */
export function publishRestaurantFloorWork(seats: number | null): void {
  if (SEATS() !== seats) {
    SEATS.set(seats);
  }
}

/**
 * The floor's open work (kzd r1b WI-0b; DESIGN-r1b-floor-off §3.1, RL-5 = a): the tables still seated. With any open,
 * switching the Floor plan off WINDS it down — hidden from the rail, the open tables finish, no new seating — and the
 * platform retires it by itself when the last one is paid or cleared (D356). Unknown is `pending`: never "nothing
 * open". Registered at the root, as retail's and inventory's are, so the Control Centre and the module's Setup get the
 * same answer whatever screen is open.
 */
export function restaurantFloorWork(seats: number | null): OpenWork | null {
  if (seats === null) {
    return { kind: 'docs', n: 0, label: 'tables still open', pending: true };
  }
  return seats > 0
    ? { kind: 'docs', n: seats, label: 'tables still open', resolve: 'Pay or clear them on the Floor — new orders go to the counter.' }
    : null;
}

/** Registers the floor's open work with the capability service; it contributes no rail badge of its own. */
export function restaurantOpenWorkProjections(): readonly AttentionProjection[] {
  inject(CapabilityService).registerOpenWork({
    moduleId: 'restaurant',
    capKey: 'floor',
    work: computed(() => restaurantFloorWork(SEATS())),
  });
  return [];
}

/**
 * Restaurant's rail badges (kz2 M3; the DS's `restaurant.attention.ts`, D156–D158), registered at
 * the root: the Kitchen's `late || active`, its tone saying which (D157), and the Floor's tables in
 * service (D65 F). Each carries its capability, so a capability that is off shows no count.
 */
export function restaurantAttentionProjections(): readonly AttentionProjection[] {
  return [
    {
      rule: { id: 'restaurant.kitchen.active', moduleId: 'restaurant', capKey: 'kitchen', railItemId: RESTAURANT_RAIL.kitchen, enabled: true, scope: 'site' },
      count: computed(() => KITCHEN().count),
      tone: computed(() => KITCHEN().tone),
    },
    {
      rule: { id: 'restaurant.floor.occupied', moduleId: 'restaurant', capKey: 'floor', railItemId: RESTAURANT_RAIL.floor, enabled: true, scope: 'site' },
      count: OCCUPIED.asReadonly(),
    },
  ];
}

/** Register the restaurant's projections at the root (AppModule), so the shell can show them. */
export function provideRestaurantAttention(): Provider[] {
  return [
    { provide: ATTENTION_PROJECTIONS, useValue: restaurantAttentionProjections, multi: true },
    { provide: ATTENTION_PROJECTIONS, useValue: restaurantOpenWorkProjections, multi: true },
  ];
}
